"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatCurrency } from "@/lib/utils";
import {
  Car,
  CreditCard,
  DollarSign,
  XCircle,
  Users,
  TrendingUp,
  AlertTriangle,
} from "lucide-react";

interface ExecutiveSummaryProps {
  data: {
    date: string;
    totalWashes: number;
    subscriptionsUsed: number;
    oneTimeOrders: number;
    cashCollected: number;
    cancelled: number;
    staffWorkload: Array<{
      staffId: string;
      staffName: string;
      appointments: number;
      percentage: number;
    }>;
    workloadImbalance: boolean;
  };
}

export function ExecutiveSummary({ data }: ExecutiveSummaryProps) {
  // Get top 2 staff by appointments, excluding orphaned/unknown staff records
  const topStaff = data.staffWorkload
    .filter(
      (member) =>
        member.staffName && member.staffName.toLowerCase() !== "unknown",
    )
    .sort((a, b) => b.appointments - a.appointments)
    .slice(0, 2);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold mb-2">Executive Summary</h2>
        <p className="text-sm text-muted-foreground">
          Today's key metrics at a glance
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* Total Washes Today */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Washes Today
            </CardTitle>
            <Car className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.totalWashes}</div>
            <p className="text-xs text-muted-foreground">
              Completed appointments
            </p>
          </CardContent>
        </Card>

        {/* Active Subscriptions Used */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Subscriptions Used
            </CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.subscriptionsUsed}</div>
            <p className="text-xs text-muted-foreground">
              Active subscriptions used today
            </p>
          </CardContent>
        </Card>

        {/* One-Time Orders */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              One-Time Orders
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.oneTimeOrders}</div>
            <p className="text-xs text-muted-foreground">
              Package bookings today
            </p>
          </CardContent>
        </Card>

        {/* Cash Collected */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Cash Collected
            </CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(data.cashCollected)}
            </div>
            <p className="text-xs text-muted-foreground">Cash payments today</p>
          </CardContent>
        </Card>

        {/* Cancelled/No-shows */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Cancelled / No-shows
            </CardTitle>
            <XCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {data.cancelled}
            </div>
            <p className="text-xs text-muted-foreground">
              Cancelled appointments
            </p>
          </CardContent>
        </Card>

        {/* Staff Workload Split */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Staff Workload Split
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {topStaff.length > 0 ? (
              <div className="space-y-2">
                {topStaff.map((member, idx) => (
                  <div
                    key={member.staffId}
                    className="flex items-center justify-between"
                  >
                    <span className="text-sm font-medium">
                      {member.staffName}
                    </span>
                    <Badge variant={idx === 0 ? "default" : "secondary"}>
                      {member.percentage}%
                    </Badge>
                  </div>
                ))}
                {data.workloadImbalance && (
                  <div className="flex items-center gap-1 text-xs text-amber-600 mt-2">
                    <AlertTriangle className="h-3 w-3" />
                    <span>Workload imbalance detected</span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No data available</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
